const GENERIC_PUBLIC_LABELS = new Set([
  "membre",
  "member",
  "profil",
  "profile",
  "user",
  "utilisateur",
]);

const EMAIL_PROVIDER_PATTERN =
  /(?:^|[._-])(gmail|googlemail|yahoo|hotmail|outlook|icloud|protonmail|proton|live|msn|orange|sfr|free|laposte)(?:[._-](?:com|fr|net|org))?(?:$|[._-])/i;
const DOMAIN_SUFFIX_PATTERN =
  /^[a-z0-9._-]+[._-][a-z0-9-]+[._-](?:com|fr|net|org|io|co|eu)$/i;
const OAUTH_PROFILE_PROVIDER_IDS = new Set(["facebook.com", "google.com"]);

export function formatPublicUsername(value) {
  return String(value || "").trim().replace(/^@+/, "");
}

export function isUnsafePublicIdentityLabel(value) {
  const normalized = formatPublicUsername(value).toLowerCase();

  if (!normalized) {
    return true;
  }

  return (
    normalized.includes("@") ||
    GENERIC_PUBLIC_LABELS.has(normalized) ||
    EMAIL_PROVIDER_PATTERN.test(normalized) ||
    DOMAIN_SUFFIX_PATTERN.test(normalized)
  );
}

export function getPublicUsername(user = {}) {
  const username = formatPublicUsername(user?.username);
  return isUnsafePublicIdentityLabel(username) ? "" : username;
}

export function resolvePublicIdentityLabel(user = {}, fallback = "Utilisateur") {
  const username = getPublicUsername(user);

  if (username) {
    return username;
  }

  for (const value of [user?.displayName, user?.name]) {
    const label = formatPublicUsername(value);
    if (!isUnsafePublicIdentityLabel(label)) {
      return label;
    }
  }

  return fallback;
}

export function buildOAuthUsernameSuggestion(user = {}, maxLength = 32) {
  const providerProfiles = Array.isArray(user?.providerData)
    ? user.providerData
    : [];
  const oauthProfile = providerProfiles.find((providerProfile) =>
    OAUTH_PROFILE_PROVIDER_IDS.has(
      String(providerProfile?.providerId || "").trim().toLowerCase()
    )
  );
  const primaryProviderId = String(user?.provider || "")
    .trim()
    .toLowerCase();
  const sourceName =
    oauthProfile?.displayName ||
    (OAUTH_PROFILE_PROVIDER_IDS.has(primaryProviderId)
      ? user?.displayName || user?.name
      : "");
  const suggestion = String(sourceName || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^@+/, "")
    .replace(/[^a-z0-9._-]+/g, ".")
    .replace(/[.]{2,}/g, ".")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, maxLength);

  return isUnsafePublicIdentityLabel(suggestion) ? "" : suggestion;
}

function normalizeUsernameSuggestion(value, maxLength) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^@+/, "")
    .replace(/[^a-z0-9._-]+/g, ".")
    .replace(/[.]{2,}/g, ".")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, maxLength);
}

/**
 * Propose un pseudo public modifiable sans jamais exposer le domaine e-mail.
 * OAuth fournit en priorité un nom public. Pour un compte e-mail, seule la
 * partie locale (avant @) est utilisée. Un alias neutre fondé sur l'UID sert
 * de dernier recours lorsque cette partie est vide, générique ou trop courte.
 */
export function buildPublicUsernameSuggestion(user = {}, maxLength = 32) {
  const oauthSuggestion = buildOAuthUsernameSuggestion(user, maxLength);
  if (oauthSuggestion) return oauthSuggestion;

  const emailLocalPart = String(user?.email || "").split("@")[0];
  const emailSuggestion = normalizeUsernameSuggestion(emailLocalPart, maxLength);
  if (
    emailSuggestion.length >= 3 &&
    !isUnsafePublicIdentityLabel(emailSuggestion)
  ) {
    return emailSuggestion;
  }

  const uidFragment = normalizeUsernameSuggestion(
    String(user?.uid || user?.id || "").replace(/[^a-z0-9]/gi, ""),
    8
  );
  if (!uidFragment) return "";

  const fallbackSuggestion = normalizeUsernameSuggestion(
    `membre-${uidFragment}`,
    maxLength
  );
  return fallbackSuggestion.length >= 3 &&
    !isUnsafePublicIdentityLabel(fallbackSuggestion)
    ? fallbackSuggestion
    : "";
}
